import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, BarChart3, Download, FileText, Users, Clock, ClipboardCheck, AlertTriangle, Repeat, ArrowLeft } from 'lucide-react';
import { reportsLibrary } from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const KIND_ICON = {
  revenue_by_rma: Users, matter_aging: Clock, ready_to_lodge: ClipboardCheck,
  vevo_expiry: AlertTriangle, retention: Repeat,
};
const KIND_DESCRIPTION = {
  revenue_by_rma: 'Money in per RMA, paid vs unpaid, matter throughput.',
  matter_aging: 'Every open matter bucketed by age so nothing rots.',
  ready_to_lodge: 'Matters where all boxes are ticked and money is in trust.',
  vevo_expiry: 'Visa expirations in the next 6 months — the retention pipeline.',
  retention: 'Repeat clients and their lifetime spend with the firm.',
};

export default function ReportsLibraryPage() {
  const [library, setLibrary] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [exporting, setExporting] = React.useState('');

  React.useEffect(() => {
    reportsLibrary.library().then((d) => setLibrary(d.items || []))
      .catch(() => toast.error('Failed to load library'));
  }, []);

  const run = async (kind) => {
    setSelected(kind);
    setResult(null);
    setRunning(true);
    try {
      const r = await reportsLibrary.run({ kind });
      setResult(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Report failed');
    } finally { setRunning(false); }
  };

  const download = async (fmt) => {
    if (!selected) return;
    setExporting(fmt);
    try {
      const blob = fmt === 'pdf' ? await reportsLibrary.exportPdf({ kind: selected })
                                 : await reportsLibrary.exportCsv({ kind: selected });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ImmiPro-${selected}-${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`${fmt.toUpperCase()} export failed`);
    } finally { setExporting(''); }
  };

  return (
    <div className="p-8" data-testid="reports-library-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A3A5C]">Reports library</h1>
          <p className="mt-1 text-sm text-slate-500">Board-ready canned reports — revenue, aging, retention, VEVO expiry. Download as CSV or PDF.</p>
        </div>
        <Link to="/app/reports"><Button variant="outline" data-testid="reports-lib-back"><ArrowLeft className="mr-2 h-4 w-4" /> Custom reports</Button></Link>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {library.map((r) => {
          const Icon = KIND_ICON[r.kind] || BarChart3;
          const isSelected = selected === r.kind;
          return (
            <Card key={r.kind} className={`cursor-pointer transition ${isSelected ? 'border-[#2ECC9A] bg-emerald-50/40' : 'hover:border-slate-300'}`}
                  onClick={() => run(r.kind)} data-testid={`reports-lib-card-${r.kind}`}>
              <CardContent className="p-5">
                <div className="mb-2 flex items-center gap-2">
                  <div className={`rounded-full p-2 ${isSelected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="font-serif text-base font-semibold text-[#1A3A5C]">{r.label}</div>
                </div>
                <p className="text-xs text-slate-500">{KIND_DESCRIPTION[r.kind]}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {(running || result) && (
        <Card className="mt-6" data-testid="reports-lib-result">
          <CardContent className="p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-serif text-xl font-bold text-[#1A3A5C]">{result?.label || 'Running…'}</div>
                {result && <div className="text-xs text-slate-500">{result.count} row{result.count === 1 ? '' : 's'} · generated {new Date(result.generated_at).toLocaleString('en-AU')}</div>}
              </div>
              {result && result.count > 0 && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => download('csv')} disabled={!!exporting} data-testid="reports-lib-csv">
                    {exporting === 'csv' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}CSV
                  </Button>
                  <Button variant="outline" onClick={() => download('pdf')} disabled={!!exporting} data-testid="reports-lib-pdf">
                    {exporting === 'pdf' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}PDF
                  </Button>
                </div>
              )}
            </div>
            {running ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
            ) : result?.rows?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                    <tr>{result.columns.map((c) => <th key={c.key} className="px-3 py-2">{c.label}</th>)}</tr>
                  </thead>
                  <tbody>
                    {result.rows.slice(0, 50).map((row, i) => (
                      <tr key={i} className="border-b border-slate-50" data-testid={`reports-lib-row-${i}`}>
                        {result.columns.map((c) => {
                          const v = row[c.key];
                          const isNum = typeof v === 'number';
                          const isMoney = c.key.includes('revenue') || c.key.includes('total') || c.key.includes('balance');
                          return (
                            <td key={c.key} className={`px-3 py-2 ${isNum ? 'font-semibold text-[#1A3A5C]' : 'text-slate-700'}`}>
                              {isMoney && isNum ? `A$${v.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : (v === '' || v == null) ? '—' : v}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {result.rows.length > 50 && (
                  <div className="mt-3 text-center text-xs text-slate-500">Showing first 50 of {result.count} rows — download CSV or PDF for the full list</div>
                )}
              </div>
            ) : (
              <div className="py-10 text-center text-sm text-slate-500">No rows for this report.</div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
